import type { GenerationMode } from '../model/generation-mode'
import type { AiModel, ModelCapabilityParameters, ModelCatalogSnapshot, ModelParameterRule } from '../model/ai-model'
import type { ModelCatalogResponse } from './model-catalog-contract'
import { getGenerationModeByCapability } from '../model/generation-mode'
import { modelPresentationStrengthLabels } from '../model/model-presentation'

type ApiCapability = ModelCatalogResponse['data'][number]['capabilities'][number]
type ApiParameterRule = NonNullable<ApiCapability['parameterSchema']>['properties'][string]

function mapParameterRule(rule: ApiParameterRule): ModelParameterRule {
  const values = (rule.enum ?? []).filter((value): value is string | number | boolean => ['string', 'number', 'boolean'].includes(typeof value))
  return {
    ...(rule.type ? { type: rule.type } : {}),
    description: rule.description ?? '',
    enum: values,
    ...(rule.minimum !== null && rule.minimum !== undefined ? { minimum: rule.minimum } : {}),
    ...(rule.maximum !== null && rule.maximum !== undefined ? { maximum: rule.maximum } : {}),
    ...(rule.minLength !== null && rule.minLength !== undefined ? { minLength: rule.minLength } : {}),
    ...(rule.maxLength !== null && rule.maxLength !== undefined ? { maxLength: rule.maxLength } : {}),
    ...(rule.minItems !== null && rule.minItems !== undefined ? { minItems: rule.minItems } : {}),
    ...(rule.maxItems !== null && rule.maxItems !== undefined ? { maxItems: rule.maxItems } : {}),
  }
}

function mapCapabilityParameters(item: ModelCatalogResponse['data'][number]) {
  const mapped: Partial<Record<GenerationMode, ModelCapabilityParameters>> = {}
  item.capabilities.forEach(({ capability, parameterSchema }) => {
    const mode = getGenerationModeByCapability(capability)
    if (!mode || !parameterSchema) return
    mapped[mode] = {
      required: parameterSchema.required,
      properties: Object.fromEntries(Object.entries(parameterSchema.properties).map(([key, rule]) => [key, mapParameterRule(rule)])),
      additionalProperties: parameterSchema.additionalProperties ?? false,
    }
  })
  return mapped
}

function mapCapabilityPresentations(item: ModelCatalogResponse['data'][number]) {
  const mapped: AiModel['capabilityPresentations'] = {}
  item.capabilities.forEach(({ capability, presentation }) => {
    const mode = getGenerationModeByCapability(capability)
    if (!mode) return
    mapped[mode] = {
      badge: presentation?.badge ?? null,
      positioning: presentation?.positioning ?? null,
      strengths: presentation?.strengths ?? [],
      shortDescription: presentation?.shortDescription ?? null,
      sortOrder: presentation?.sortOrder ?? 0,
    }
  })
  return mapped
}

export function mapModelCatalogResponse(response: ModelCatalogResponse): ModelCatalogSnapshot {
  const models: AiModel[] = response.data.flatMap((item, index) => {
    const capabilities = item.capabilities.flatMap(({ capability }) => {
      const mode = getGenerationModeByCapability(capability)
      return mode ? [mode] : []
    })
    if (capabilities.length === 0) return []
    const mediaKind = capabilities.includes('digital-human') ? 'avatar' : capabilities.some((capability) => capability === 'text-to-video' || capability === 'image-to-video' || capability === 'video-generation') ? 'video' : 'image'
    const capabilityParameters = mapCapabilityParameters(item)
    const capabilityPresentations = mapCapabilityPresentations(item)
    const resolutionTags = capabilities.flatMap((mode) => capabilityParameters[mode]?.properties.resolution?.enum.filter((value): value is string => typeof value === 'string') ?? [])
    const presentationTags = capabilities.flatMap((mode) => capabilityPresentations[mode]?.strengths.map((strength) => modelPresentationStrengthLabels[strength]) ?? [])
    return [{
      id: String(item.id),
      name: item.name,
      provider: '',
      version: '',
      description: item.description ?? '',
      capabilities,
      mediaKind,
      status: 'active',
      recommended: capabilities.some((mode) => capabilityPresentations[mode]?.badge === 'RECOMMENDED'),
      tags: [...new Set([...presentationTags, ...resolutionTags])],
      supportedRatios: capabilities.flatMap((mode) => capabilityParameters[mode]?.properties.aspectRatio?.enum.filter((value): value is string => typeof value === 'string') ?? []),
      pricing: { credits: 0, unit: mediaKind === 'video' ? 'video' : mediaKind === 'image' ? 'image' : 'generation' },
      speedScore: 0,
      qualityScore: 0,
      updatedAt: '',
      accent: (['violet', 'cyan', 'amber', 'rose'] as const)[index % 4] ?? 'violet',
      capabilityParameters,
      capabilityPresentations,
    }]
  })
  return { models, version: `api-${Date.now()}`, fetchedAt: Date.now(), source: 'api' }
}
